import { HelmetProvider } from "react-helmet-async";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
// routes
import Router from "./pages/routes";
// theme
import ThemeProvider from "./theme";
// components
import { StyledChart } from "./components/chart";
import ScrollToTop from "./components/scroll-to-top";
import eventBus from "./common/EventBus";

import { logOut, selectCurrentToken } from "./features/auth/authSlice";
import { useRefreshMutation } from "./features/auth/authApiSlice";
import Loading from "./components/spinners/Loading";
import { initializeSocket, disconnectSocket, onNotification, offNotification } from "./services/socket.service";
import { addNotification } from "./features/notifications/notificationsSlice";
import { apiSlice } from "./app/api/apiSlice";
import { useUpdateActivityMutation } from "./features/users/usersApiSlice";

const ACTIVITY_INTERVAL_MS = 5 * 60 * 1000; // update lastActivity every 5 minutes

// ----------------------------------------------------------------------

export default function App() {
  const dispatch = useDispatch();
  const [{ isLoading }] = useRefreshMutation();
  const token = useSelector(selectCurrentToken);
  const [updateActivity] = useUpdateActivityMutation();

  const handleLogOut = useCallback(() => {
    dispatch(logOut());
  }, [dispatch]);

  useEffect(() => {
    eventBus.on("logout", () => {
      handleLogOut();
    });

    return () => {
      eventBus.remove("logout");
    };
  }, [handleLogOut]);

  // Periodically update lastActivity while the user is logged in
  useEffect(() => {
    if (!token) return;

    // Send immediately on login/page-load, then every ACTIVITY_INTERVAL_MS
    updateActivity();
    const intervalId = setInterval(() => {
      updateActivity();
    }, ACTIVITY_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [token, updateActivity]);

  // Initialize Socket.IO connection when token is available
  useEffect(() => {
    if (token) {
      initializeSocket(token);

      // Listen for notification events
      const handleNotification = (notification) => {
        console.log("📬 Received notification:", notification);
        dispatch(addNotification(notification));

        // Invalidate notification cache to refetch
        dispatch(apiSlice.util.invalidateTags(["Notification"]));
      };

      onNotification(handleNotification);

      return () => {
        offNotification();
      };
    } else {
      // Disconnect socket when logged out
      disconnectSocket();
    }
  }, [token, dispatch]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <HelmetProvider>
      <ThemeProvider>
        <ScrollToTop />
        <StyledChart />
        <Router />
      </ThemeProvider>
    </HelmetProvider>
  );
}
